import React, { useState } from 'react';
import CenteredModal from 'components/UI/Modal/CenteredModal';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import styles from './SenderSetupModal.module.scss';

interface SenderSetupModalProps {
    currentEmail: string | null;
    locationName?: string;
    onClose: () => void;
    // Resolves false when it couldn't be saved; the modal stays open so the email can be fixed.
    onSave: (email: string) => Promise<boolean> | void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SenderSetupModal: React.FC<SenderSetupModalProps> = ({ currentEmail, locationName, onClose, onSave }) => {
    const [email, setEmail] = useState(currentEmail ?? '');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (saving) return;
        if (!EMAIL_REGEX.test(email.trim())) {
            setError('Enter a valid email address.');
            return;
        }
        setSaving(true);
        const saved = await onSave(email.trim());
        setSaving(false);
        if (saved !== false) onClose();
    };

    return (
        <CenteredModal
            isOpen
            onClose={onClose}
            maxWidth="small"
        >
            <CenteredModal.Header title={currentEmail ? 'Change sender email' : 'Set up sender email'} />
            <CenteredModal.Content padding="large">
                <div className={styles.form}>
                    <p className={styles.text}>
                        {locationName && <strong>{locationName}. </strong>}
                        We&apos;ll send a verification link to this address. This location can be included in campaigns
                        once it&apos;s confirmed.
                    </p>
                    <Input
                        label="Sender email"
                        placeholder="marketing@yourbrand.com"
                        value={email}
                        error={error}
                        autoFocus={false}
                        onChange={event => {
                            setEmail(event.target.value);
                            setError(null);
                        }}
                        onEnterPressed={handleSave}
                    />
                    <div className={styles.actions}>
                        <Button
                            variant="outlined"
                            label="Cancel"
                            onClick={onClose}
                        />
                        <Button
                            variant="black"
                            label={saving ? 'Saving…' : 'Save & verify'}
                            disabled={saving}
                            onClick={handleSave}
                        />
                    </div>
                </div>
            </CenteredModal.Content>
        </CenteredModal>
    );
};

export default SenderSetupModal;
